// ─── Tóm tắt kết quả thao tác theo nhóm ────────────────────────────
//
// Các endpoint nhóm (login/logout/start/stop/restart) trả về mảng kết quả
// TỪNG tài khoản kèm lý do lỗi cụ thể, nhưng UI trước đây chỉ hiện `message`
// tổng ở cấp response — khách thấy đúng một dòng "Đăng nhập nhóm thất bại
// (tất cả tài khoản lỗi)" mà không biết acc nào lỗi, lỗi gì.

/** Một phần tử trong `data[]` của response thao tác nhóm. */
export interface IGroupActionResult {
  accountId: string;
  success: boolean;
  error?: string;
  /** Cảnh báo KHÔNG chặn (VD số dư mỏng) — đi kèm cả kết quả success. */
  warning?: string;
  data?: unknown;
}

/** Tra username từ accountId — response chỉ có id, tên nằm ở state của FE. */
export type UsernameLookup = (accountId: string) => string | undefined;

const FALLBACK_ERROR = 'Lỗi không xác định';

/** Số nhóm lỗi tối đa hiển thị — tránh toast dài vô tận khi nhóm đông. */
const MAX_REASON_GROUPS = 4;

export interface IGroupActionSummary {
  total: number;
  successCount: number;
  failCount: number;
  /** Đã gộp theo lý do — cùng một lỗi thì gom các tài khoản vào một dòng. */
  message: string;
  /**
   * Cảnh báo không chặn, gom từ mọi kết quả (kể cả success).
   * Tách khỏi `message` vì đây KHÔNG phải lỗi: thao tác vẫn thành công, chỉ cần
   * báo cho khách biết. Trộn vào `message` sẽ làm toast thành công đổi sang đỏ.
   */
  warnings: string[];
}

function labelOf(result: IGroupActionResult, lookup?: UsernameLookup): string {
  const name = lookup?.(result.accountId);
  if (name) return name;
  // Không tra được tên thì cắt id cho đỡ rối, vẫn đủ để đối chiếu
  return result.accountId ? `#${result.accountId.slice(-6)}` : 'không rõ';
}

/**
 * Gộp kết quả thành một thông báo đọc được.
 *
 * Gom theo LÝ DO chứ không liệt kê từng tài khoản: thực tế cả nhóm thường
 * chết cùng một nguyên nhân (proxy dead, token hết hạn), gom lại thành
 * "2 tài khoản: proxy không hoạt động — acc1, acc2" thay vì lặp lại lý do
 * dài dòng cho từng dòng.
 */
export function summarizeGroupAction(
  results: IGroupActionResult[] | undefined | null,
  action: string,
  lookup?: UsernameLookup,
): IGroupActionSummary {
  const list = Array.isArray(results) ? results : [];
  const failed = list.filter((r) => !r.success);
  const successCount = list.length - failed.length;
  const warnings = list
    .map((r) => (r.warning || '').trim())
    .filter((w) => w.length > 0);

  if (list.length === 0) {
    return {
      total: 0,
      successCount: 0,
      failCount: 0,
      message: `${action}: không có tài khoản nào`,
      warnings,
    };
  }

  if (failed.length === 0) {
    return {
      total: list.length,
      successCount,
      failCount: 0,
      message: `${action} thành công (${successCount} tài khoản)`,
      warnings,
    };
  }

  const byReason = new Map<string, string[]>();
  for (const r of failed) {
    const reason = (r.error || FALLBACK_ERROR).trim();
    const bucket = byReason.get(reason) ?? [];
    bucket.push(labelOf(r, lookup));
    byReason.set(reason, bucket);
  }

  const groups = [...byReason.entries()];
  const shown = groups.slice(0, MAX_REASON_GROUPS);
  const hiddenAccounts = groups
    .slice(MAX_REASON_GROUPS)
    .reduce((sum, [, names]) => sum + names.length, 0);

  const header =
    successCount === 0
      ? `${action} thất bại — cả ${failed.length} tài khoản đều lỗi:`
      : `${action}: ${successCount} thành công, ${failed.length} lỗi:`;

  const lines = shown.map(([reason, names]) => `• ${names.join(', ')} — ${reason}`);
  if (hiddenAccounts > 0) lines.push(`• …và ${hiddenAccounts} tài khoản khác`);

  return {
    total: list.length,
    successCount,
    failCount: failed.length,
    message: [header, ...lines].join('\n'),
    warnings,
  };
}
